import { c as _c } from "react/compiler-runtime";
import { IncomingHttpHeaders } from 'http';
import { ApolloClient } from '@apollo/client';
import { ContextStore } from '@src/contexts/AppContextStore/context-store';
import { initContextStore } from '@src/lib/CustomSSR/context-store';
import { createEmotionCache } from '@src/lib/createEmotionCache';
import { DESIGN_SYSTEM_COOKIE, getDesignSystemOverride } from '@src/lib/designSystemOverride';
import { parse as parseCookie } from 'cookie';
import { NextPage, NextPageContext } from 'next';
import NextApp from 'next/app';
import { AppContext } from 'next/app';
import { NextRouter } from 'next/dist/client/router';
import React from 'react';
import { ReactElement } from 'react';
import initApolloClient from './apollo';
export interface WithCustomSSROptions {
  getApolloClient?: any;
  getApolloDataFromTree?: (tree: ReactElement, context?: {
    [key: string]: any;
  }, renderFunction?: any) => Promise<any>;
  getContextStore?: any;
  getContextDataFromTree?: (tree?: ReactElement) => Promise<any>;
  render?: (props: {
    Page: NextPage<any>;
    props: any;
  }) => any;
  onError?: (error: Error, ctx?: NextPageContext) => void;
}
export interface WithApolloState {
  data?: any;
}
export interface WithContextState {}
export interface WithCustomSSRProps<TCache = any> {
  apolloClient: ApolloClient;
  apolloState: WithApolloState;
  contextStore: ContextStore;
  contextState: WithContextState;
  router: NextRouter;
}
export interface InitApolloClientOptions<TCache = any> {
  ctx?: NextPageContext;
  headers?: IncomingHttpHeaders;
  router?: NextRouter;
  apolloClient?: TCache;
  apolloState?: any;
}
export type InitContextStore = (options: InitContextStoreOptions) => ContextStore;
export type InitContextStoreOptions = {
  ctx?: NextPageContext;
  headers?: IncomingHttpHeaders;
  router?: NextRouter;
  contextStore?: ContextStore;
  contextState?: any;
};
export type InitApolloClient = (options: InitApolloClientOptions) => ApolloClient;
export interface ApolloPageContext extends NextPageContext {
  apolloClient?: ApolloClient;
  apolloState?: any;
}
export interface ApolloAppContext<C = any> extends AppContext {
  ctx: ApolloPageContext;
  AppTree: any;
  apolloClient?: ApolloClient;
  apolloState?: any;
}
export type ApolloContext = ApolloPageContext | ApolloAppContext;

// Gets the display name of a JSX component for dev tools
function getDisplayName(Component: React.ComponentType<any>) {
  return Component.displayName || Component.name || 'Unknown';
}
export default function withCustomSSR(options: WithCustomSSROptions) {
  return (Page: NextPage<any> | typeof NextApp, pageOptions: WithCustomSSROptions = {}) => {
    const getInitialProps = Page.getInitialProps;
    const getContextDataFromTree = 'getContextDataFromTree' in pageOptions ? pageOptions.getContextDataFromTree : options.getContextDataFromTree;
    const getApolloDataFromTree = 'getApolloDataFromTree' in pageOptions ? pageOptions.getApolloDataFromTree : options.getApolloDataFromTree;
    const onError = pageOptions.onError || options.onError;
    function WithCustomSSR(t0) {
      const $ = _c(12);
      let apolloClient;
      let apolloState;
      let contextState;
      let contextStore;
      let props;
      if ($[0] !== t0) {
        ({
          apolloClient,
          apolloState,
          contextStore,
          contextState,
          ...props
        } = t0);
        $[0] = t0;
        $[1] = apolloClient;
        $[2] = apolloState;
        $[3] = contextState;
        $[4] = contextStore;
        $[5] = props;
      } else {
        apolloClient = $[1];
        apolloState = $[2];
        contextState = $[3];
        contextStore = $[4];
        props = $[5];
      }
      let t1;
      if ($[6] !== apolloClient || $[7] !== apolloState || $[8] !== contextState || $[9] !== contextStore || $[10] !== props) {
        t1 = <Page {...props} apolloClient={apolloClient} apolloState={apolloState} contextState={contextState} contextStore={contextStore} />;
        $[6] = apolloClient;
        $[7] = apolloState;
        $[8] = contextState;
        $[9] = contextStore;
        $[10] = props;
        $[11] = t1;
      } else {
        t1 = $[11];
      }
      return t1;
    }
    WithCustomSSR.displayName = `WithCustomSSR(${getDisplayName(Page)})`;
    WithCustomSSR.getInitialProps = async (pageCtx: ApolloContext & {
      contextState: any;
    }) => {
      const ctx = 'Component' in pageCtx ? pageCtx.ctx : pageCtx;
      if (ctx?.req?.url && ctx.req.url.indexOf('_next/data') !== -1) {
        return {};
      }
      const router = 'Component' in pageCtx ? pageCtx.router : undefined;
      const {
        AppTree
      } = pageCtx;
      const headers = ctx.req ? ctx.req.headers : {};
      const userAgent = headers['user-agent'] || (typeof navigator !== 'undefined' ? navigator?.userAgent : null);
      const cookies = headers['cookie'] ? parseCookie(headers['cookie']) : {};
      const initialWindowWidth = cookies['scoWindowWidth'] ? parseInt(cookies['scoWindowWidth']) : null;
      const scoDevPin = cookies['scoDevPin'];
      const designSystemOverride = getDesignSystemOverride(cookies[DESIGN_SYSTEM_COOKIE]);
      let apolloState: WithApolloState = pageCtx?.apolloState || {};
      const apolloClient: ApolloClient = pageCtx?.apolloClient || initApolloClient(options.getApolloClient, {
        ctx,
        headers,
        router,
        apolloState
      });
      let contextState: WithContextState = pageCtx?.contextState || {};
      const contextStore = initContextStore(options.getContextStore, {
        contextState
      });
      let pageProps = {};
      if (getInitialProps) {
        ctx.apolloClient = apolloClient;
        pageProps = await getInitialProps(pageCtx as any);
      }
      if (typeof window === 'undefined') {
        if (ctx.res && (ctx.res.headersSent || ctx.res.writableEnded)) {
          return pageProps;
        }
        try {
          const emotionCache = createEmotionCache();
          const apolloRenderResult = await getApolloDataFromTree(<AppTree {...'Component' in pageCtx ? {
            ...pageProps
          } : {
            pageProps: {
              ...pageProps
            }
          }} apolloClient={apolloClient} apolloState={apolloState} contextState={contextState} contextStore={contextStore} designSystemOverride={designSystemOverride} emotionCache={emotionCache} initialWindowWidth={initialWindowWidth} renderPass={'APOLLO_SSR'} scoDevPin={scoDevPin} userAgent={userAgent} />);
        } catch (error) {
          if (onError) {
            onError(error as Error, ctx);
          } else {
            // Prevent Apollo Client GraphQL errors from crashing SSR.
            if (process.env.NODE_ENV !== 'production') {
              // tslint:disable-next-line no-console This is a necessary debugging log
              console.error('SSR Error [getDataFromTree]', error);
            }
          }
        }

        // Collect all the info for final render pass!
        apolloState = {
          data: apolloClient.extract()
        };
        const ssrStatusCode = contextStore.get('_ssrStatusCode');
        if (ssrStatusCode && ctx.res) {
          ctx.res.statusCode = ssrStatusCode;
        }
        contextStore.set('_ssrStatusCode', undefined);
        contextState = contextStore.getAll();
      }

      // Prevent serializing circular structures
      ;
      (apolloClient as any).toJSON = () => {
        return null;
      };
      (contextStore as any).toJSON = () => {
        return null;
      };
      const initialProps = {
        ...pageProps,
        apolloClient,
        apolloState,
        contextStore,
        contextState,
        designSystemOverride,
        initialWindowWidth,
        userAgent,
        scoDevPin
      };
      return initialProps;
    };
    return WithCustomSSR;
  };
}